import { Box, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import getProReport from '../../../../api/pro';
import MuiSelectField from '../../../../common-components/form-fields/mui-select-field';
import {
  getProVesselTypeLabel,
  ProReportResponse,
  ProVesselTypes,
} from '../../../../models/pro.model';
import ProReportPreview from '../../../../reporting/pro-report-html-preview';

import useUserRoleAndShortId from '../../../../hooks/user-details/useUserDetails';
import ProReportEmailDownloadButton from '../../../../reporting/pro-report-email/email-download';
import ProReportPdfDownloadButton from '../../../../reporting/pro-report-pdf/pro-report-pdf-download';
import {
  getAnalyticsVesselType,
  getProReportAnalyticsEventLabels,
  getRoleAnalyticsEventLabel,
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../../../utils/analytics';
import { Port } from '../../world-ports.model';

type FormValues = {
  vesselType: string;
};

interface ProReportErrorProps {
  fetchError: string | null;
  port: Port;
}

function ProReportError({ fetchError, port }: ProReportErrorProps) {
  if (!fetchError) return null;
  if (fetchError === '404') {
    return (
      <Typography
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: 700,
          gap: 1,
          m: 1,
        }}
      >
        <span>
          No PRO report available for{' '}
          <Box component="span" sx={{ fontWeight: 'bold', display: 'inline' }}>
            {port.NAME}
          </Box>{' '}
          as port is currently considered to present a low risk, ports are
          continually being assessed and updated.
        </span>
      </Typography>
    );
  }
  return <Typography sx={{ m: 1 }}>{fetchError}</Typography>;
}

interface ProTabProps {
  port: Port;
}

function ProTab({ port }: ProTabProps) {
  const userDetails = useUserRoleAndShortId();
  const [currentReport, setCurrentReport] = useState<ProReportResponse | null>(
    null
  );
  const [selectedVesselType, setSelectedVesselType] = useState<ProVesselTypes>(
    ProVesselTypes.NO_VESSEL_TYPE
  );
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchReport = async () => {
      setFetching(true);
      setFetchError(null);
      setCurrentReport(null);

      const vesselTypeParam =
        selectedVesselType === ProVesselTypes.NO_VESSEL_TYPE
          ? undefined
          : selectedVesselType;

      const dataPromises: Promise<any>[] = [];

      const proReportPromise = getProReport(port.UNLOCODE, vesselTypeParam);
      dataPromises.push(proReportPromise);

      try {
        const [report] = await Promise.all(dataPromises);
        if (isMounted) {
          setCurrentReport(report);
          logAnalyticsEvent('Ports', 'ProReportFetched', [
            getUserAnalyticsEventLabel(userDetails),
            getRoleAnalyticsEventLabel(userDetails),
            ...getProReportAnalyticsEventLabels(
              port.UNLOCODE,
              getAnalyticsVesselType(selectedVesselType)
            ),
          ]);
        }
      } catch (error: any) {
        if (isMounted) {
          if (error?.response?.status === 404) {
            setFetchError('404');
          } else {
            setFetchError('An error occurred while fetching the PRO report.');
          }
        }
      } finally {
        if (isMounted) setFetching(false);
      }
    };
    fetchReport();
    return () => {
      isMounted = false;
    };
  }, [selectedVesselType, port]);

  const vesselTypesOptions = [
    ...Object.values(ProVesselTypes)
      .filter((vesselType) => vesselType !== ProVesselTypes.UNKNOWN)
      .map((vesselType) => ({
        name: getProVesselTypeLabel(vesselType) as string,
        value: vesselType as string,
      })),
  ];

  function VesselTypeSync({
    value,
    onChange,
  }: {
    value: ProVesselTypes;
    onChange: (v: ProVesselTypes) => void;
  }) {
    useEffect(() => {
      onChange(value);
    }, [value, onChange]);
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        scrollbarWidth: 'thin',
        py: 1,
        px: 2,
      }}
    >
      <Box sx={{ mt: 1, pb: 1 }}>
        <Formik<FormValues>
          initialValues={{ vesselType: ProVesselTypes.NO_VESSEL_TYPE }}
          onSubmit={() => {}}
        >
          {({ values }) => (
            <>
              <VesselTypeSync
                value={values.vesselType as ProVesselTypes}
                onChange={setSelectedVesselType}
              />
              <MuiSelectField
                name="vesselType"
                label="Vessel Type"
                placeholder="Select vessel type"
                options={vesselTypesOptions}
                ariaLabel="select-vessel-type"
              />
            </>
          )}
        </Formik>
      </Box>

      <Box
        sx={{
          overflowY: 'auto',
          minHeight: '80px',
          pb: 1,
        }}
      >
        {currentReport && (
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6">PRO Report Preview</Typography>
          </Box>
        )}
        <Box sx={{ minHeight: 0 }}>
          {fetching && (
            <Typography sx={{ m: 1 }}>
              <CircularProgress color="success" size={12} /> Fetching PRO
              report...
            </Typography>
          )}
          <ProReportError fetchError={fetchError} port={port} />
          {currentReport && <ProReportPreview report={currentReport} />}
        </Box>
      </Box>
      {currentReport && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            gap: '10px',
            p: '10px',
          }}
        >
          <ProReportPdfDownloadButton
            port={port}
            report={currentReport}
            selectedVesselType={selectedVesselType}
            fetching={fetching}
          />
          <ProReportEmailDownloadButton
            port={port}
            report={currentReport}
            selectedVesselType={selectedVesselType}
            fetching={fetching}
          />
        </Box>
      )}
    </Box>
  );
}

export default ProTab;
